import { useEffect, useState } from 'react';
import { useEscapeLayer } from '../../ui/useEscapeLayer';

/** The manifest fields the credits show. */
export interface CreditEntry {
  id: string;
  variant: number;
  kind: string;
  source: { url?: string; author?: string; title?: string; license?: string };
  credit?: string | null;
  modifications?: string;
}

/** One line per source: variants of an id and repeated sources collapse. */
export function creditLines(entries: readonly CreditEntry[]) {
  const lines = new Map<
    string,
    { ids: string[]; title: string; author: string; license: string; url: string; credit: string }
  >();
  for (const e of entries) {
    const s = e.source;
    const key = `${s.url ?? ''}|${s.title ?? ''}|${s.author ?? ''}`;
    let line = lines.get(key);
    if (!line) {
      line = {
        ids: [],
        title: s.title ?? e.id,
        author: s.author ?? 'unknown',
        license: s.license ?? 'unknown',
        url: s.url ?? '',
        credit: e.credit ?? '',
      };
      lines.set(key, line);
    }
    if (!line.ids.includes(e.id)) line.ids.push(e.id);
  }
  return [...lines.values()];
}

/** Loaded on demand, so the manifest never ships in the home entry. */
async function loadManifest(): Promise<CreditEntry[]> {
  const m = (await import('../../../../../assets/audio/manifest.json')) as unknown as {
    default: { entries: CreditEntry[] };
  };
  return m.default.entries;
}

/** Credits modal: every sound's source, author and licence, from the audio manifest (DROW-120). */
export function Credits({ onClose }: { onClose: () => void }) {
  const [entries, setEntries] = useState<CreditEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    loadManifest().then(
      (e) => live && setEntries(e),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, []);
  // Its own Esc layer: over Options, Esc closes only the credits.
  useEscapeLayer(onClose);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section
        className="panel credits"
        role="dialog"
        aria-modal="true"
        aria-label="Credits"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2>Credits</h2>
          <button data-sound="back" aria-label="Close credits" onClick={onClose}>
            ✕
          </button>
        </header>
        <h3>Sound</h3>
        {failed && <p className="muted">Could not load the credits.</p>}
        {!entries && !failed && <p className="muted">Loading…</p>}
        {entries && (
          <ul className="credit-list">
            {creditLines(entries).map((l) => (
              <li key={`${l.url}|${l.title}|${l.author}`}>
                <span className="credit-title">
                  {l.url ? (
                    <a href={l.url} target="_blank" rel="noreferrer noopener">
                      {l.title}
                    </a>
                  ) : (
                    l.title
                  )}
                </span>{' '}
                by {l.author} · {l.license}
                {l.credit && <span className="muted"> · {l.credit}</span>}
                <span className="muted credit-ids"> ({l.ids.join(', ')})</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
